'use client'

/**
 * Per-type leaderboard tables for /data/most-efficient — brand-standard
 * treatment matching the price-index, energy-index, and brand-index tables
 * (white card, gray-50 uppercase header, hairline dividers, right-aligned
 * tabular numerals, overflow-x scroll container), click-to-sort via the shared
 * SortableTable.
 *
 * Each table is ONE appliance type's top-N named winners, ranked by certified
 * annual energy use per cubic foot. Every value arrives already-derived from
 * app/data/most-efficient/page.tsx (which owns the single JSON importer);
 * sorting only reorders, it never derives. Lower kWh/yr per ft³ is better, so
 * the default sort is rank ascending — the leader on top.
 */
import { BadgeCheck, ShoppingBag } from 'lucide-react'
import { SortableTable, type SortableColumn } from '../_components/sortable-table'
import { EntityLink, type LinkTarget } from '../_components/entity-link'
import type { WinnerRow } from './most-efficient-summary'
import { fmtPerFt3, fmtKwh, fmtCap } from './most-efficient-summary'

/**
 * A winner row plus the live pages the server resolved for it (FRIDGE-334):
 * the brand's page or indexed collection, and — when the certified model is
 * stocked in the catalog — the product page. Null → plain text.
 */
export type LinkedWinnerRow = WinnerRow & { brandLink?: LinkTarget; productLink?: LinkTarget }

/** The ENERGY STAR Most Efficient chip — orange, house-palette, no shopping cue. */
function MostEfficientChip() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#ff9951]/[0.12] text-[#b25410] border border-[#ff9951]/40 px-2 py-0.5 text-[11px] font-bold whitespace-nowrap">
      <BadgeCheck className="w-3.5 h-3.5" />
      Most Efficient
    </span>
  )
}

export function LeaderboardTable({ rows }: { rows: LinkedWinnerRow[] }) {
  const columns: SortableColumn<LinkedWinnerRow>[] = [
    {
      key: 'rank',
      header: '#',
      numeric: true,
      sortValue: (r) => r.rank,
      render: (r) => <span className="font-bold text-gray-900">{r.rank}</span>,
    },
    {
      key: 'model',
      header: 'Model',
      sortValue: (r) => `${r.brand} ${r.model}`,
      render: (r) => (
        <div className="min-w-0">
          <EntityLink link={r.brandLink} className="font-semibold text-[#0469f0]">
            {r.brand}
          </EntityLink>
          {/* FRIDGE-1507 T: mt-1.5 puts the model link 24px+ below the brand
              link, centre to centre, so their 24px hit areas do not overlap. */}
          <span className="mt-1.5 block text-[12px] text-gray-500 font-mono">
            {r.productLink ? (
              <EntityLink link={r.productLink} title="View this model on Fridge.com">
                <span className="inline-flex items-center gap-1">
                  {r.model}
                  <ShoppingBag className="w-3 h-3 text-[#ff9951]" aria-hidden="true" />
                </span>
              </EntityLink>
            ) : (
              r.model
            )}
          </span>
        </div>
      ),
    },
    {
      key: 'kwh_per_ft3',
      header: 'kWh/yr per ft³',
      numeric: true,
      sortValue: (r) => r.kwh_per_ft3,
      render: (r) => <span className="font-bold text-[#0469f0]">{fmtPerFt3(r.kwh_per_ft3)}</span>,
    },
    {
      key: 'kwh',
      header: 'kWh/yr',
      numeric: true,
      sortValue: (r) => r.kwh,
      render: (r) => <span className="font-semibold">{fmtKwh(r.kwh)}</span>,
    },
    {
      key: 'cap',
      header: 'Capacity ft³',
      numeric: true,
      sortValue: (r) => r.cap,
      render: (r) => <span className="text-gray-600">{fmtCap(r.cap)}</span>,
    },
    {
      key: 'pct',
      header: '% vs federal',
      numeric: true,
      sortValue: (r) => r.pct_less_than_federal,
      render: (r) => (
        <span className="text-gray-700">{r.pct_less_than_federal}%</span>
      ),
    },
    {
      key: 'badge',
      header: 'ENERGY STAR tier',
      sortValue: (r) => (r.meets_most_efficient ? 1 : 0),
      render: (r) => (r.meets_most_efficient ? <MostEfficientChip /> : <span className="text-gray-500">—</span>),
    },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={(r) => `${r.brand}-${r.model}-${r.rank}`}
      defaultSortKey="rank"
      defaultDir="asc"
      minWidth={860}
      caption="Most efficient models of this type, ranked by kWh/yr per cubic foot, sortable | Fridge.com"
    />
  )
}
